/* ========================================================================
   PLP 共通ユーティリティ
   通常 PLP と new-arrivals 仮想コレクションの共通処理をまとめる。
   ======================================================================== */
(() => {
  const ROOT_SELECTOR = '[data-aureme-plp-root]';
  let pendingRenderedFocusSelector = '';

  function isQueryableScope(scope) {
    return scope instanceof Element || scope instanceof Document;
  }

  function queryAll(scope, selector) {
    if (!isQueryableScope(scope)) return [];
    return Array.from(scope.querySelectorAll(selector));
  }

  function isElementTarget(target) {
    return target instanceof Element;
  }

  function getRoots(scope = document) {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      return [scope];
    }

    return queryAll(scope, ROOT_SELECTOR);
  }

  function getRootForElement(element) {
    return element?.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
  }

  function getNamedFieldNames(form) {
    return new Set(
      queryAll(form, '[name]')
        .map((input) => input.getAttribute('name'))
        .filter(Boolean),
    );
  }

  function getFilterForm(root) {
    return root?.querySelector('[data-aureme-plp-filter-form]');
  }

  function getFilterTrigger(root) {
    return root?.querySelector('[data-aureme-filter-trigger]');
  }

  function getFilterDrawerPanel(root) {
    return root?.querySelector('.aureme-plp-filter-drawer__panel');
  }

  function createSearchParams(searchParams) {
    if (searchParams instanceof URLSearchParams) {
      return new URLSearchParams(searchParams.toString());
    }

    return new URLSearchParams(typeof searchParams === 'string' ? searchParams : '');
  }

  function buildSearchParamsString(searchParams) {
    return createSearchParams(searchParams).toString();
  }

  function buildSearchUrl(searchParams) {
    const query = buildSearchParamsString(searchParams);
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  function buildFormSearchParams(form) {
    const params = new URLSearchParams();
    const formData = new FormData(form);

    formData.forEach((value, name) => {
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return;
      params.append(name, normalizedValue);
    });

    return params;
  }

  function removeSearchParamFields(searchParams, fieldNames) {
    for (const name of fieldNames) {
      searchParams.delete(name);
    }

    return searchParams;
  }

  function replaceSearchParamFields(searchParams, fieldNames, nextParams = '') {
    removeSearchParamFields(searchParams, fieldNames);

    createSearchParams(nextParams).forEach((value, name) => {
      searchParams.append(name, value);
    });

    return searchParams;
  }

  function removeSearchParamsByKey(searchParams, predicate) {
    const nextSearchParams = createSearchParams(searchParams);

    for (const key of [...nextSearchParams.keys()]) {
      if (!predicate(key)) continue;
      nextSearchParams.delete(key);
    }

    return nextSearchParams;
  }

  function parsePositiveInteger(value, fallback = 0) {
    const parsedValue = Number.parseInt(value || '', 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  }

  /*
   * FacetFiltersForm.renderPage で DOM が差し替わるため、
   * ページネーションなどの復帰先 selector だけを一時保持して描画後に focus する。
   */
  function queueFocusAfterRender(selector) {
    pendingRenderedFocusSelector = selector || '';
  }

  function clearQueuedFocusAfterRender() {
    pendingRenderedFocusSelector = '';
  }

  function focusQueuedElementAfterRender() {
    if (!pendingRenderedFocusSelector) return;

    document.querySelector(pendingRenderedFocusSelector)?.focus();
    pendingRenderedFocusSelector = '';
  }

  function getNormalizedFacetQuery(searchParams, root = document.querySelector(ROOT_SELECTOR)) {
    const normalizationResult =
      window.AuremePlp && typeof window.AuremePlp.normalizeSearchParams === 'function'
        ? window.AuremePlp.normalizeSearchParams(searchParams, root)
        : { searchParams: buildSearchParamsString(searchParams), didChange: false };

    return typeof normalizationResult.searchParams === 'string'
      ? normalizationResult.searchParams
      : buildSearchParamsString(normalizationResult.searchParams);
  }

  function requestFacetRender(searchParams, root = document.querySelector(ROOT_SELECTOR)) {
    const query = getNormalizedFacetQuery(searchParams, root);

    /* 商品一覧本体は Dawn の section 再描画を流用し、未読込時だけ通常遷移へフォールバックする */
    if (
      typeof FacetFiltersForm !== 'undefined' &&
      typeof FacetFiltersForm.renderPage === 'function'
    ) {
      FacetFiltersForm.renderPage(query, null, true);
      return;
    }

    window.location.search = query;
  }

  function syncFilterOptionState(option) {
    const input = option.querySelector('input');
    if (!input) return;

    option.classList.toggle('is-selected', input.checked);
    option.classList.toggle('is-disabled', input.disabled);
  }

  function syncFilterOptionStates(scope = document) {
    for (const option of queryAll(scope, '[data-aureme-filter-option]')) {
      syncFilterOptionState(option);
    }
  }

  window.AuremePlpUtils = Object.assign(window.AuremePlpUtils || {}, {
    ROOT_SELECTOR,
    buildFormSearchParams,
    buildSearchParamsString,
    buildSearchUrl,
    clearQueuedFocusAfterRender,
    createSearchParams,
    focusQueuedElementAfterRender,
    getFilterDrawerPanel,
    getFilterForm,
    getFilterTrigger,
    getNamedFieldNames,
    getRootForElement,
    getRoots,
    isElementTarget,
    parsePositiveInteger,
    queueFocusAfterRender,
    removeSearchParamFields,
    removeSearchParamsByKey,
    replaceSearchParamFields,
    requestFacetRender,
    syncFilterOptionState,
    syncFilterOptionStates,
  });

  window.AuremePlp = Object.assign(window.AuremePlp || {}, {
    clearQueuedFocusAfterRender,
  });
})();
